<!--
  ImpersonationBanner.vue

  PR-R1 shipped the slot + DOM scaffolding; PR-R3 wired the banner to the
  Pinia impersonation store. Rendered by `SuperAdminLayout.vue` (and any
  future DistributorLayout instance where a SA is viewing-as) only when
  an impersonation session is active.

  Single source of truth: `useImpersonationStore()`. The banner subscribes
  to the store; the SA Distributors-list "Impersonate" button calls
  store.startImpersonation(), and the banner exit button below calls
  store.stopImpersonation().

  Audit log: the backend impersonate endpoints are responsible for
  emitting audit rows on start + stop. The frontend does NOT post audit
  rows directly. See impersonationService.js docstring for the contract.
-->
<template>
    <div
        v-if="store.isActive"
        class="impersonation-banner"
        role="alert"
        data-testid="impersonation-banner"
    >
        <span class="impersonation-banner__icon" aria-hidden="true">!</span>
        <span
            v-if="store.isClientActive"
            class="impersonation-banner__text"
            data-testid="impersonation-banner-client-text"
        >
            Viewing as
            <strong>{{ store.clientName || "a client" }}</strong>
            (client)
        </span>
        <span v-else class="impersonation-banner__text">
            Viewing as
            <strong>{{ store.distributorName || "another distributor" }}</strong>
            (impersonation session)
        </span>
        <button
            type="button"
            class="impersonation-banner__exit"
            data-testid="impersonation-banner-exit"
            @click="onExit"
        >
            Stop impersonating
        </button>
    </div>
</template>

<script>
import { describeError } from "@/utils/safeErrorLog";
import { useImpersonationStore } from "@/stores/impersonation";

const BANNER_HEIGHT_CSS_VAR = "--tf-impersonation-banner-height";

export default {
    name: "ImpersonationBanner",
    setup() {
        const store = useImpersonationStore();
        return { store };
    },
    mounted() {
        this._installBannerHeightObserver();
    },
    updated() {
        // v-if toggles the banner element; re-attach the observer if the
        // banner became visible after mount.
        this._installBannerHeightObserver();
    },
    beforeUnmount() {
        this._teardownBannerHeightObserver();
    },
    methods: {
        // PR-H: publish the rendered banner height as a CSS custom
        // property on document.documentElement so a fixed-position surface
        // can offset itself below the banner instead of overlapping it.
        // No surface reads it today: StoreEditShell's .ces-pager used to,
        // and is now a sticky row inside the scroll area (below the banner
        // by construction). Kept for the next fixed-position surface.
        // The observer instance is held off the reactive surface
        // (this._ro) so Vue does not wrap it in a proxy.
        _installBannerHeightObserver() {
            if (typeof window === "undefined") return;
            if (typeof ResizeObserver === "undefined") return;
            const el = this.$el && this.$el.nodeType === 1 ? this.$el : null;
            if (!el) {
                // Banner v-if'd out -> ensure the var reflects "no banner".
                document.documentElement.style.setProperty(
                    BANNER_HEIGHT_CSS_VAR,
                    "0px",
                );
                return;
            }
            if (this._ro && this._observedEl === el) return;
            this._teardownBannerHeightObserver();
            this._ro = new ResizeObserver((entries) => {
                for (const entry of entries) {
                    // PR-H2: read offsetHeight (border-box, includes
                    // padding + border) rather than entry.contentRect
                    // (content-box only). contentRect.height was the
                    // banner's INNER text height ~31px, missing the
                    // 8px padding top/bottom + 1px border-bottom that
                    // visually push the banner to ~48px. Using the
                    // smaller value left the pager overlapping the
                    // banner by the padding height.
                    const target = entry.target;
                    const h = Math.round(
                        (target && target.offsetHeight) ||
                            entry.contentRect.height,
                    );
                    document.documentElement.style.setProperty(
                        BANNER_HEIGHT_CSS_VAR,
                        `${h}px`,
                    );
                }
            });
            this._ro.observe(el);
            this._observedEl = el;
        },
        _teardownBannerHeightObserver() {
            if (this._ro) {
                this._ro.disconnect();
                this._ro = null;
            }
            this._observedEl = null;
            if (typeof document !== "undefined") {
                document.documentElement.style.setProperty(
                    BANNER_HEIGHT_CSS_VAR,
                    "0px",
                );
            }
        },
        async onExit() {
            // Capture the exit target BEFORE stop clears the mode. Precedence:
            //   1. store.exitTo -- set by a distributor "View as client"
            //      session to '/distributor/clients' (a distributor has no
            //      /admin access and would be bounced by the /admin guard).
            //   2. SA client impersonation -> the SA Clients list.
            //   3. distributor impersonation -> /admin.
            const exitTarget =
                this.store.exitTo ||
                (this.store.isClientActive ? "/admin/clients" : "/admin");
            try {
                await this.store.stopImpersonation();
                // Hard-navigate to the admin app. A soft $router.push is
                // bounced by the /admin requireCapability("superadmin.access")
                // guard: stopping clears the impersonation server-side, but the
                // SPA's in-memory capability set still reflects the impersonated
                // session until the page reloads. The full reload (mirroring the
                // START path in DistributorList.doImpersonate) forces the SPA to
                // re-fetch the restored SuperAdmin capabilities so the guard lets
                // /admin through.
                window.location.assign(exitTarget);
            } catch (err) {
                // Surface but do not crash the layout. The store leaves
                // state intact on failure, so the banner stays visible
                // and the SA can retry.
                // eslint-disable-next-line no-console
                console.error("Stop impersonation failed:", describeError(err));
            }
        },
    },
};
</script>

<style scoped>
.impersonation-banner {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 16px;
    background: #fff4e5;
    border-bottom: 1px solid #f3b94c;
    color: #6b4500;
    font-size: 0.875rem;
}
.impersonation-banner__icon {
    background: #f3b94c;
    color: white;
    border-radius: 50%;
    width: 20px;
    height: 20px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
}
.impersonation-banner__text {
    flex: 1;
}
.impersonation-banner__exit {
    background: white;
    border: 1px solid #f3b94c;
    color: #6b4500;
    padding: 4px 12px;
    border-radius: 4px;
    cursor: pointer;
}
.impersonation-banner__exit:hover {
    background: #f3b94c;
    color: white;
}
</style>
